import React, {useCallback} from 'react';

import {Skeleton} from '@mui/material';
import {createStyles, withStyles, type WithStyles} from '@mui/styles';
import type {Theme} from '@netx/core-components/Theme';

import PermissionListItem, {
	type PermissionListItemProps,
	type PermissionItemMenuProps,
	type MinimumPermissionItemMenuProps,
} from '../ListItem';

import type {AttributePermissionIndexed} from '../../types';
import type {VirtualizedListItemProps} from '@netx/core-components/VirtualizedList';

export interface VirtualizedPermissionListItemDataProps<
	MenuProps extends MinimumPermissionItemMenuProps = PermissionItemMenuProps,
> extends Omit<PermissionListItemProps<MenuProps>, 'permission' | 'onClick'> {
	onClick?: (index: number) => void;
}

export interface VirtualizedPermissionListItemProps<
	MenuProps extends MinimumPermissionItemMenuProps = PermissionItemMenuProps,
> extends VirtualizedListItemProps<
		AttributePermissionIndexed,
		VirtualizedPermissionListItemDataProps<MenuProps>
	> {}

const styles = (theme: Theme) =>
	createStyles({
		skeleton: {
			height: 'calc(100% - 1px)',
			width: '100%',
			position: 'absolute',
			top: 0,
			left: 0,
			bottom: 0,
			right: 0,
		},
	});

const VirtualizedPermissionListItem = <
	MenuProps extends MinimumPermissionItemMenuProps = PermissionItemMenuProps,
>({
	classes,
	data,
	index,
	style,
}: VirtualizedPermissionListItemProps<MenuProps> &
	WithStyles<typeof styles>) => {
	const {rows, onClick, viewportWidth, ...props} = data;

	const permission = rows[index];

	const handleClick = useCallback(() => onClick?.(index), [index, onClick]);

	if (!permission)
		return (
			<div style={style}>
				<Skeleton className={classes.skeleton} variant="rectangular" />
			</div>
		);

	return (
		<PermissionListItem<MenuProps>
			id={`permission-${index.toString()}`}
			onClick={handleClick}
			permission={permission}
			role="group"
			{...props}
			style={{...style, ...props.style}}
		/>
	);
};

export default withStyles(styles)(
	React.memo(VirtualizedPermissionListItem),
) as <
	MenuProps extends MinimumPermissionItemMenuProps = PermissionItemMenuProps,
>(
	props: Omit<VirtualizedPermissionListItemProps<MenuProps>, 'classes'>,
) => JSX.Element;
